import { useQuery } from '@tanstack/react-query'
import api from '../../../api'
import { cn } from '../../../lib/utils'
import { AiInsightCard } from '../../finance/AiInsightCard'
import { DetailSkeleton } from '../../finance/SkeletonStates'
import { formatCurrency } from '../../../config/chartTheme'
import { BookOpen, FileCheck } from 'lucide-react'

interface Level6ResolutionProps {
  exceptionId: number
}

export function Level6Resolution({ exceptionId }: Level6ResolutionProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['exception-resolution', exceptionId],
    queryFn: () => api.get(`/exceptions/${exceptionId}`).then(r => r.data),
  })

  if (isLoading) return <DetailSkeleton />

  const exc = data || {}
  const result = exc.resolution || exc.result || exc.exception_result || {}
  const jePreview = result.journal_entry || result.je_preview || {}
  const jeLines = jePreview.lines || jePreview.entries || []

  return (
    <div className="space-y-4">
      {/* Resolution Header */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-5',
        'border-l-[3px] border-l-emerald-500',
      )}>
        <div className="flex items-center gap-2 mb-3">
          <FileCheck className="h-4 w-4 text-emerald-500" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Resolution Detail
          </span>
          {result.resolution_mode && (
            <span className={cn(
              'text-[10px] px-1.5 py-0.5 rounded',
              result.resolution_mode === 'MODE_1'
                ? 'bg-emerald-500/10 text-emerald-400'
                : 'bg-purple-500/10 text-purple-400',
            )}>
              {result.resolution_mode}
            </span>
          )}
        </div>

        {result.confidence_score != null && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[11px] text-[var(--text-muted,#64748B)]">Confidence:</span>
            <div className="flex gap-0.5">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1.5 w-3 rounded-full',
                    i < Math.round((result.confidence_score || 0) / 10)
                      ? 'bg-emerald-500/60'
                      : 'bg-white/[0.06]',
                  )}
                />
              ))}
            </div>
            <span className="font-['JetBrains_Mono',monospace] text-[11px] text-emerald-400">
              {result.confidence_score}%
            </span>
          </div>
        )}
      </div>

      {/* AI Analysis */}
      {(result.recommendation_summary || result.analysis) && (
        <AiInsightCard
          title="AI Analysis"
          description={result.recommendation_summary || result.analysis || ''}
          confidence={result.confidence_score}
        />
      )}

      {/* Root Cause */}
      {result.root_cause && (
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
        )}>
          <div className="flex items-center gap-1.5 mb-2">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Root Cause
            </span>
          </div>
          <p className="text-[13px] leading-relaxed text-[var(--text-secondary,#94A3B8)]">
            {result.root_cause}
          </p>
        </div>
      )}

      {/* Cited Standard */}
      {result.cited_standard && (
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
        )}>
          <div className="flex items-center gap-1.5 mb-2">
            <BookOpen className="h-3.5 w-3.5 text-[var(--text-muted,#64748B)]" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Cited Standard
            </span>
          </div>
          <p className="text-[13px] text-[var(--text-secondary,#94A3B8)]">
            {result.cited_standard}
          </p>
        </div>
      )}

      {/* JE Preview */}
      {jeLines.length > 0 && (
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
        )}>
          <div className="flex items-center gap-1.5 mb-3">
            <div className="h-1.5 w-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Proposed Journal Entry
            </span>
          </div>
          <div className="space-y-1">
            {jeLines.map((line: any, i: number) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-white/[0.02]">
                <div className="flex items-center gap-3">
                  <span className="font-['JetBrains_Mono',monospace] text-[12px] text-[var(--text-muted,#64748B)]">
                    {line.account_code}
                  </span>
                  <span className="text-[12px] text-[var(--text-secondary,#94A3B8)]">
                    {line.account_name || line.description || ''}
                  </span>
                </div>
                <div className="flex gap-6">
                  <span className="font-['JetBrains_Mono',monospace] text-[12px] text-[var(--text-primary,#F0F2F5)] w-24 text-right">
                    {line.debit_amount ? formatCurrency(line.debit_amount, false) : ''}
                  </span>
                  <span className="font-['JetBrains_Mono',monospace] text-[12px] text-[var(--text-primary,#F0F2F5)] w-24 text-right">
                    {line.credit_amount ? formatCurrency(line.credit_amount, false) : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
